import React from "react";

import { renderToStaticMarkup } from "react-dom/server";

import TooltipLines from "@/src/components/tooltip/TooltipLines";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/src/components/ui/avatar";
import { APP_TOOLTIP_ID } from "@/src/constants/tooltip";

import { getInitials } from "../../common/AvatarStack";
import DataTableColumnHeader from "../../dataTable/DataTableColumnHeader";
import { dateColumn, statusColumn } from "../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../dataTable/DataTableLinkCell";
import { BULKS_ROUTE } from "../../routes/routes";
import { getBulkStatusBucket } from "../bulkStatus";

import type { TFunction } from "i18next";

import type {
  BulkRow,
  GetIndexBulkListResponse,
} from "../../../api/BulkAPI.types";
import type {
  ColumnLayout,
  DataTableColumnDef,
  DataTablePage,
} from "../../dataTable/dataTable.types";

interface BulkColumnsOptions {
  t: TFunction;
}

export function getBulkHref(uuid: string): string {
  return `${BULKS_ROUTE}/${uuid}`;
}

export function selectBulkPage(
  response: GetIndexBulkListResponse,
): DataTablePage<BulkRow> {
  return {
    rows: response.data.map((bulk) => ({
      ...bulk,
      status: getBulkStatusBucket(bulk.status),
    })),
    rowCount: response.page.total_items,
  };
}

const UUID_LAYOUT: ColumnLayout = { minWidth: 325 };
const OWNER_LAYOUT: ColumnLayout = {
  width: "9%",
  minWidth: 85,
  align: "center",
};

export function bulkColumns({
  t,
}: BulkColumnsOptions): DataTableColumnDef<BulkRow>[] {
  return [
    statusColumn<BulkRow>("status", t("status"), [
      { value: "active", label: t("common:bulk-active"), tone: "success" },
      { value: "pause", label: t("common:bulk-pause"), tone: "warning" },
      { value: "stop", label: t("common:bulk-stop"), tone: "error" },
    ]),
    {
      accessorKey: "uuid",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("title")} />
      ),
      meta: { ...UUID_LAYOUT, searchable: true },
      cell: ({ row }) => (
        <DataTableLinkCell href={getBulkHref(row.original.uuid)}>
          {row.original.uuid}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "filename",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("filename")} />
      ),
      meta: { searchable: true },
      cell: ({ row }) => row.original.filename,
    },
    {
      accessorKey: "owner",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("owner")} />
      ),
      enableSorting: false,
      meta: OWNER_LAYOUT,
      cell: ({ row }) => {
        const { owner } = row.original;
        const ownerTooltip = renderToStaticMarkup(
          <TooltipLines lines={[owner.name, owner.company, owner.email]} />,
        );

        return (
          <span
            className="inline-flex"
            data-tooltip-html={ownerTooltip || undefined}
            data-tooltip-id={ownerTooltip ? APP_TOOLTIP_ID : undefined}
          >
            <Avatar size="sm">
              <AvatarImage alt="" src={owner.avatar_url ?? undefined} />
              <AvatarFallback className="text-xs">
                {getInitials(owner.name ?? owner.email)}
              </AvatarFallback>
            </Avatar>
          </span>
        );
      },
    },
    dateColumn<BulkRow>("inserted_at", t("inserted_at")),
  ];
}
